import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { useApprovalsPaged } from "@/common/artemis-operations/approvals";
import { ApprovalTable } from "@/components/approvals/approval-table";
import { BasePageContainer } from "@/components/base";
import { useActions } from "@/contexts/actions-context";
import { usePageTitle } from "@/contexts/page-title-context";

export const Route = createFileRoute(
	"/_authenticated/$orgSlug/workspaces/$workspaceId/approvals/",
)({
	component: Approvals,
	// e.g. ?search=<taskId> from the task details "View approval" link.
	validateSearch: (
		s: Record<string, unknown>,
	): { search?: string; status?: string } => ({
		search: typeof s.search === "string" ? s.search : undefined,
		// Pre-selects the status filter, e.g. the dashboard's "Pending approvals" card.
		status: typeof s.status === "string" ? s.status : undefined,
	}),
});

function Approvals() {
	const { workspaceId } = Route.useParams();
	const { search: initialSearch, status: initialStatus } = Route.useSearch();
	const { setTitle } = usePageTitle();
	const { setActions } = useActions();
	const list = useApprovalsPaged(workspaceId, initialSearch, initialStatus);

	useEffect(() => {
		setTitle("Approvals");
		setActions([]);
	}, [setTitle, setActions]);

	return (
		<BasePageContainer>
			<ApprovalTable
				workspaceId={workspaceId}
				data={list.rows}
				loading={list.isFetching}
				totalCount={list.totalCount}
				hasNextPage={list.hasNextPage}
				hasPreviousPage={list.hasPreviousPage}
				pageIndex={list.pageIndex}
				onNextPage={list.onNextPage}
				onPreviousPage={list.onPreviousPage}
				pageSize={50}
				manualSorting
				initialSorting={list.sort}
				onSortingChange={list.setSort}
				search={list.search}
				onSearchChange={list.setSearch}
				status={list.filters.status}
				onStatusChange={(v) => list.setFilter("status", v)}
				riskLevel={list.filters.riskLevel}
				onRiskLevelChange={(v) => list.setFilter("riskLevel", v)}
				onRefresh={() => list.refetch()}
			/>
		</BasePageContainer>
	);
}
